import { Moment } from 'moment';
import Axios, { CancelTokenSource } from 'axios';
import {
    AggregationFunction,
    AnalyticsEngine,
    AnalyticsFilter,
    AnalyticsGroupBy,
    AnalyticsProjection,
    AnalyticsSort,
} from '@sprinklr/stories/analytics/AnalyticsRequest';
import AnalyticsService from '../../AnalyticsService/AnalyticsService';
import { Post, PostProfile } from '@sprinklr/stories/post/Post';
import { PostsEngine, PostsRequest } from '@sprinklr/stories/post/PostsRequest';
import TimePeriod from 'models/TimePeriod/TimePeriod';
import { WidgetDimensionKey } from 'utils/Widget/WidgetDimensionKey';
import {
    default as SprinklrAPIService,
    PromiseWithCancel,
} from 'services/SprinklrAPIService/SprinklrAPIService';
import PostsService from '../PostsService';
import { PostsProvider, PostsProviderDefault } from './PostsProvider';
import TimePeriodService from 'services/TimePeriodService/TimePeriodService';
import { WidgetSort } from 'utils/Widget/Sort/WidgetSort';
import ObjectUtils from 'utils/ObjectUtils/ObjectUtils';
import { ReportingRequest, ReportingResponse } from '@sprinklr/stories/reporting/types';
import { buildProjectionHeading, dedupeProjections } from '@sprinklr/stories/analytics/helpers';
import { toReportingGroupBy } from '@sprinklr/stories-convert/request/toReportingRequests';

export interface ProjectionDecor {
    value: any;
    additional: boolean;
    dataType?: string;
    origOrder?: number;
    alternateHeading?: string;
}

interface PostResponse {
    posts: Post[];
    postIds: string[];
}

const decorators = ['CHANGE', 'PERCENTAGE_CHANGE', 'PERCENTAGE'];

// Put your desired lookup dimensions into this map
const dimensionsLookup = [
    { id: 'ACCOUNT_GROUP_ID', header: 'Account Group', values: [] }, // PAID
    { id: 'paidInitiativeId', header: 'paidInitiativeId', values: [] }, // PAID
    { id: 'BRAND_ID', header: 'Brand', values: [] }, // BENCHMARKING
    { id: 'CAMPAIGN_ID', header: 'Campaign', values: [] }, // PLATFORM
    { id: 'SUB_CAMPAIGN_ID', header: 'Sub-Campaign', values: [] },
    { id: 'ADVOCATES', header: 'Top Advocates', values: [] }, // LISTENING
    { id: 'BRAND_REPUTATION_L1', header: 'Brand Reputation - L1', values: [] },
    { id: 'BRAND_REPUTATION_L2', header: 'Brand Reputation - L2', values: [] },
    { id: 'BRAND_REPUTATION_L3', header: 'Brand Reputation - L3', values: [] },
    { id: 'GLOBAL_CATEGORY_L1', header: 'Global Category - L1', values: [] },
    { id: 'GLOBAL_CATEGORY_L2', header: 'Global Category - L2', values: [] },
    { id: 'GLOBAL_CATEGORY_L3', header: 'Global Category - L3', values: [] },
    { id: 'TOPIC_IDS', header: 'Topic', values: [] }, // LISTENING
    { id: 'adName', header: 'Ad Variant Name', values: [] },
    { id: 'SENTIMENT', header: 'Sentiment', values: [] },
    { id: 'LOCATION_IDS', header: 'Business_Location', values: [] },
    { id: 'LST_THEME', header: 'Theme', values: [] },

    // { id: "CAMPAIGN_ID_DIMENSION", header: "Campaign Id", values: [] }, // PLATFORM. 500 response
];

export default class PostsProviderReporting implements PostsProvider {
    constructor(private readonly sprinklrAPIService: SprinklrAPIService) {}

    private isActiveStory(engine: PostsEngine, groupBys: AnalyticsGroupBy[]) {
        return (
            engine === 'STORY_MESSAGE' &&
            groupBys?.length &&
            groupBys.some(groupBy => groupBy.dimensionName === 'STORY')
        );
    }

    request(request: PostsRequest, cancelSource?: CancelTokenSource): PromiseWithCancel<Post[]> {
        let requests: PostsRequest[] = [ObjectUtils.copy(request)];

        const postGroupByOffset = this.prepareRequest(requests[0]);

        // TODO: gross cast to any
        const additionalRequests = AnalyticsService.splitRequests(requests[0] as any) as any;
        if (additionalRequests) {
            requests = requests.concat(additionalRequests);
        }

        const promise = this.requestInternal(
            requests[0],
            null,
            postGroupByOffset,
            cancelSource
        ).then(firstResult => {
            if (!additionalRequests) {
                return firstResult.posts;
            } else {
                // Remove first request
                const remaining = requests.slice();
                remaining.shift();

                const promises = remaining.map(request => {
                    return this.requestInternal(
                        request,
                        firstResult.postIds,
                        postGroupByOffset,
                        cancelSource
                    );
                });

                return Promise.all(promises).then(results => {
                    return PostsProviderReporting.combinePosts({
                        requests: remaining,
                        firstResult: firstResult,
                        results: results,
                        requestProjections: request.projections,
                        groupByOffset: request.groupBys?.length ?? 0,
                    });
                });
            }
        });

        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        (promise as any).cancel = (message?: string) => {
            cancelSource.cancel(message);
        };

        return promise;
    }

    static normalizeProjections(
        projections: AnalyticsProjection[] = [],
        engineMeta?: PostsProviderDefault,
        additional?: boolean
    ) {
        return projections
            ?.filter(projection => !!projection.measurementName && !!projection.heading)
            .map(projection => ({
                ...projection,
                heading: buildProjectionHeading(
                    projection.measurementName,
                    projection.details?.origReport ?? engineMeta?.defaultReport,
                    projection.aggregateFunction
                ),
                details: {
                    ...projection.details,
                    label: projection.details?.label ?? projection.heading.replace(/_/g, ' '),
                    // Normalize the origReport for each projection, if not defined.
                    // Needed so splitRequests() works correctly
                    origReport: projection.details?.origReport ?? engineMeta?.defaultReport,
                    additional,
                },
            }));
    }

    // Massages the request in-place so that it can be correctly processed for split request
    prepareRequest(request: PostsRequest): number {
        const engine: PostsEngine = request.reportingEngine || 'PLATFORM';
        const engineMeta = PostsService.engines[engine];

        if (!engineMeta) {
            throw new Error('Engine "' + request.reportingEngine + '" not supported');
        }

        // We want to remove the topic groups filter if a topic ids filter is present
        const topicIdsFilter = request.filters?.find(
            (filter: AnalyticsFilter) =>
                filter.dimensionName === 'TOPIC_IDS' && filter.filterType === 'IN'
        );

        AnalyticsService.applyProjectionFilters(request);

        request.projections = PostsProviderReporting.normalizeProjections(
            request.projections,
            engineMeta,
            true
        );

        const filters = request.filters
            ?.filter((filter: AnalyticsFilter) => {
                return (
                    filter.dimensionName &&
                    filter.filterType &&
                    filter.values &&
                    filter.values.length > 0 &&
                    !(
                        topicIdsFilter &&
                        filter.dimensionName === 'TOPIC_GROUP_IDS' &&
                        filter.filterType === 'IN'
                    )
                );
            })
            .map(filter => {
                // Ensure filters have details
                if (!filter.details) {
                    filter.details = {};
                }
                return filter;
            });

        const projections: AnalyticsProjection[] = [];
        const groupBys: AnalyticsGroupBy[] = [];
        request.sorts = request.sorts?.filter(sort => !!sort.heading);
        let sorts: AnalyticsSort[] = request.sorts || [];

        // Note: If there's no sort defined in Space, then no sort is applied to query, so engineMeta.defaultSortKey is no longer used
        sorts = sorts.map((sort: AnalyticsSort) => {
            let heading = WidgetSort.getHeading(sort);

            if (sort.isDimension === true) {
                // If the request already has the sort dimension in its groupBys, update sort heading to match
                let groupBy: AnalyticsGroupBy = request.groupBys?.find(
                    item => item.dimensionName === heading
                );

                if (groupBy) {
                    heading = groupBy.heading;
                } else {
                    groupBy = {
                        heading,
                        dimensionName: heading as any,
                        groupType: 'FIELD',
                    };
                }

                // Sort groupBy needs to go to top of groupBy list before postKey
                groupBys.push(groupBy);
            } else if (engine !== 'RDB_FIREHOSE') {
                // TODO: Comment to explain why RDB_FIREHOSE bypasses projection sorts

                const report = WidgetSort.getReport(sort) || request.report;

                // If first projection, then change the report for the request
                if (!projections.length) {
                    request.report = report;
                }

                // If the request already has the sort measurement in its projections, update sort heading to match
                let projection: AnalyticsProjection = request.projections?.find(
                    item => item.measurementName === heading
                );

                let aggregateFunction: AggregationFunction = 'SUM';
                // If STATUS_ID projection for PAID isn't CARDINALITY, you get error response
                if (request.report === 'PAID' && sort.heading === engineMeta.defaultSortKey) {
                    aggregateFunction = 'CARDINALITY';
                }

                if (projection) {
                    heading = projection.heading;
                } else {
                    const measurementName = heading;
                    heading = buildProjectionHeading(heading, report, aggregateFunction);
                    projection = {
                        heading,
                        measurementName,
                        aggregateFunction,
                        details: {
                            origReport: report,
                        },
                    };
                }

                projections.push(projection);
            }

            return {
                heading,
                order: sort.order,
            };
        });

        // Add the postKey after any sorting groupBys
        const postKey = request.postDimension || engineMeta.postKey;
        let postGroupByOffset = groupBys.length;
        groupBys.push({
            heading: postKey,
            dimensionName: postKey,
            groupType: 'FIELD',
        });

        // If the user specified any additional projections, then add them (for post card feature)
        // These might have a custom label, so they should have priority over any duplicate defaults that will be added next
        if (request.projections?.length) {
            projections.push(...request.projections);
            request['extraProjections'] = request.projections;

            // Metric aggregateFunctions that are "CHANGE", "PERCENTAGE_CHANGE" or "PERCENTAGE" need
            // to be handled by adding a projectionDecorations array to the ReportingRequest
            const projectionDecorators = request.projections
                .filter((projection: AnalyticsProjection) => {
                    return decorators.indexOf(projection.aggregateFunction) !== -1;
                })
                .map(
                    projection => projection.aggregateFunction
                    // Rows come back in this order: "CHANGE", "PERCENTAGE_CHANGE", "PERCENTAGE", no
                    // matter what order the decorations are in.  Pre-sorting decorations makes
                    // post-processing more efficient.
                )
                .sort((a, b) => {
                    let result = a.localeCompare(b);
                    // PERCENTAGE_CHANGE comes before PERCENTAGE
                    if (a.charAt(0) === 'P' && b.charAt(0) === 'P') {
                        result = -result;
                    }
                    return result;
                });

            // Remove duplicate decorations
            request.projectionDecorations = projectionDecorators.filter((item, pos) => {
                return projectionDecorators.indexOf(item) === pos;
            });
        }

        // Make sure there is at least 1 projection
        // Don't add projections for listening explorer
        if (projections.length === 0 && engine !== 'RDB_FIREHOSE' && engineMeta.defaultSortKey) {
            projections.push({
                heading: buildProjectionHeading(
                    engineMeta.defaultSortKey,
                    engineMeta.defaultReport
                ),
                measurementName: engineMeta.defaultSortKey,
                aggregateFunction: 'SUM',
            });
        }

        // Add additional projections (BENCHMARKING)
        if (engineMeta.additionalProjections) {
            engineMeta.additionalProjections
                .filter(measurementName => {
                    return !projections.find(
                        projection =>
                            projection.measurementName === measurementName &&
                            projection.aggregateFunction === 'SUM'
                    );
                })
                .forEach(measurementName => {
                    projections.push({
                        heading: buildProjectionHeading(measurementName, engineMeta.defaultReport),
                        measurementName,
                        aggregateFunction: 'SUM',
                        details: {
                            origReport: engineMeta.defaultReport,
                        },
                    });
                });
        }

        // If the user specified any additional groupBys, then add them (for post card feature)
        if (request.groupBys) {
            const extraGroupBys: AnalyticsGroupBy[] = ObjectUtils.copy(request.groupBys);
            // special case for 'STORY_MESSAGE' and Active Story, need to lookup posts by Active Story
            if (this.isActiveStory(engine, extraGroupBys)) {
                groupBys.length = 0;
                postGroupByOffset = 0;
            } else {
                request['extraGroupBys'] = extraGroupBys;
            }
            groupBys.push(
                ...extraGroupBys.filter(
                    extraGroupBy =>
                        !groupBys.find(
                            groupBy => groupBy.dimensionName === extraGroupBy.dimensionName
                        )
                )
            );
        }

        request.projections = dedupeProjections(projections);
        request.groupBys = groupBys;
        request.filters = filters;
        request.sorts = sorts;

        return postGroupByOffset;
    }

    getRowKey(report: string, field: AnalyticsProjection | AnalyticsGroupBy): string {
        if (!field) {
            return null;
        }
        if (report && (field as AnalyticsProjection).measurementName) {
            return buildProjectionHeading(
                (field as AnalyticsProjection).measurementName,
                report,
                (field as AnalyticsProjection).aggregateFunction
            );
        } else if ((field as AnalyticsProjection).measurementName) {
            return (field as AnalyticsProjection).measurementName;
        } else {
            return (field as AnalyticsGroupBy).dimensionName;
        }
    }

    private requestInternal(
        request: PostsRequest,
        postIds: string[],
        postGroupByOffset: number,
        cancelSource?: CancelTokenSource
    ): PromiseWithCancel<PostResponse> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        const engine: PostsEngine = request.reportingEngine || 'PLATFORM';
        const engineMeta = PostsService.engines[engine];

        if (!engineMeta) {
            throw new Error('Engine "' + request.reportingEngine + '" not supported');
        }

        // "postDimension" overrides the default groupBy for post message
        const postKey = request.postDimension || engineMeta.postKey;

        // Filter secondary requests by the post ids we got on the first request
        if (postIds) {
            request.filters.push({
                dimensionName: 'POST_ID',
                filterType: 'IN',
                values: postIds,
            });
        }

        const timePeriod = request.timePeriod
            ? TimePeriodService.createFromAnalyticsTimePeriod(request.timePeriod)
            : TimePeriodService.createFromTimePeriodKey('last_7_days');

        const startTime: Moment = timePeriod.startDate;
        const endTime: Moment = timePeriod.endDate;

        const requestObject: ReportingRequest = {
            reportingEngine: engine as AnalyticsEngine,
            report: request.report || engineMeta.defaultReport,

            projectionDecorations: request.projectionDecorations,
            // Remap any decorator aggregate functions to "SUM"
            projections:
                request.projections &&
                request.projections.map(projection => {
                    if (decorators.indexOf(projection.aggregateFunction) !== -1) {
                        const newProjection = { ...projection };
                        newProjection.aggregateFunction = 'SUM';
                        return newProjection;
                    }

                    return projection;
                }),
            groupBys: request.groupBys ? request.groupBys.map(toReportingGroupBy) : [],
            filters: request.filters,
            projectionFilters: request.projectionFilters,
            sorts: !postIds ? request.sorts : [],

            startTime: startTime ? startTime.valueOf() : null,
            endTime: endTime ? endTime.valueOf() : null,
            timeField: request.timeField,
            timeZone: request.timePeriod ? request.timePeriod.timeZone : undefined,

            page: request.page || 0,
            pageSize: request.pageSize || 20,

            skipResolve: true,
            additional: request.additional,
        };

        if (engine === 'PAID') {
            // HACKTOWN: Required or different posts are returned from Sprinklr api.  Crazy!
            requestObject.additional = { Currency: request.additional?.Currency || 'USD' };
        }

        let promise: any = this.sprinklrAPIService.query(requestObject, cancelSource);

        promise = promise.then((response: ReportingResponse) => {
            const postIds = response.rows.map((row: any[]) => {
                return row[postGroupByOffset];
            });

            response.headings = response.headings || [];
            response.rows = response.rows || [];

            // apply translation if applicable
            response.rows.forEach((row, i) => {
                for (let x = 0; x < row.length; x++) {
                    const originalText = response.additional?.[i]?.[x]?.ORIGINAL_TEXT;
                    const translatedText = response.additional?.[i]?.[x]?.TRANSLATED_TEXT;

                    if (translatedText && row[x] === originalText) {
                        row.splice(x, x + 1, translatedText);
                    }
                }
            });

            // If any of our metrics used aggregate functions
            // CHANGE, PERCENTAGE_CHANGE, or PERCENTAGE,
            // massage the headings and rows to remove the redundant data
            if (request.projectionDecorations && request.projectionDecorations.length) {
                response.headings.forEach(heading => {
                    request.projections.forEach((projection: AnalyticsProjection) => {
                        if (projection.heading === heading) {
                            this.truncateForDecorators(
                                projection,
                                request.projectionDecorations,
                                response
                            );
                        }
                    });
                });
            }

            // These are additional fields that will appear in the post card (based on feature in core)
            const fields = [...(request.groupBys || []), ...(request.projections || [])];

            const extraProjections = request['extraProjections'] || [];
            const extraGroupBys = request['extraGroupBys'] || [];
            const extra = [...extraGroupBys, ...extraProjections];

            const projections: { [postId: string]: { [name: string]: any } } = {};
            response.rows.forEach((row: any[]) => {
                const decor = {};
                for (let i = 1; i < row.length; ++i) {
                    const heading = response.headings[i];
                    const findExtra = (extraItem: AnalyticsGroupBy | AnalyticsProjection) => {
                        if (extraItem.details?.origReport) {
                            return (
                                extraItem.heading === heading &&
                                extraItem.details?.origReport === requestObject.report
                            );
                        }
                        // if it doesn't have an origReport, it's part of the requestObject.report
                        return extraItem.heading === heading;
                    };
                    const foundExtraItem = extra.find(findExtra);
                    const foundExtraItemIndex = extra.findIndex(findExtra);
                    const origReport = foundExtraItem?.details?.origReport ?? requestObject.report;
                    const origOrder =
                        foundExtraItemIndex > -1
                            ? foundExtraItemIndex
                            : foundExtraItem?.details?.origOrder;
                    decor[heading] = this.buildDecor(
                        request,
                        i,
                        fields,
                        extra,
                        row[i] ?? 0,
                        origOrder
                    );
                }
                projections[row[postGroupByOffset]] = decor;
            });

            // special case for 'STORY_MESSAGE' and Active Story, need to lookup posts by Active Story when present
            const dimensionKey = this.isActiveStory(engine, request.groupBys) ? 'STORY' : postKey;

            return this.getPostsById(
                postIds,
                projections,
                new WidgetDimensionKey(dimensionKey),
                request.reportingEngine as AnalyticsEngine,
                request.report,
                timePeriod,
                cancelSource
            ).then((posts: any) => {
                // Find groupBy fields and save them so we can do a lookup
                if (request.groupBys && request.groupBys.length) {
                    let doLookup = false;
                    let dimensions: {
                        id: string;
                        header: string;
                        values: any[];
                        fieldName?: string;
                        srcType?: string;
                    }[] = ObjectUtils.copy(dimensionsLookup);

                    // Different engines have diffent dimension names for "Account"
                    const accountDimension = request.groupBys
                        .filter(groupBy => groupBy.heading === 'Account')
                        .map(groupBy => groupBy.dimensionName);

                    if (accountDimension.length) {
                        dimensions.push({ id: accountDimension[0], header: 'Account', values: [] });
                    }

                    // Different engines have diffent dimension names for "Account"
                    const themeTagDimension = request.groupBys
                        .filter(
                            groupBy => groupBy.dimensionName.indexOf('SPECIFIC_THEME_TAG_') === 0
                        )
                        .map(groupBy => groupBy.heading);

                    if (themeTagDimension.length) {
                        dimensions.push({
                            id: 'LST_THEME_DISPLAY_NAME',
                            header: themeTagDimension[0],
                            values: [],
                        });
                    }

                    // Do Lookup on all custom groupBys found.
                    // This was needed for this ticket:
                    //   https://sprinklr.atlassian.net/browse/DISPLAY-3960
                    request.groupBys.forEach(groupBy => {
                        if (groupBy.details?.srcType === 'CUSTOM') {
                            dimensions.push({
                                id: groupBy.dimensionName,
                                header: groupBy.details.displayName,
                                fieldName: groupBy.details.fieldName,
                                srcType: 'CUSTOM',
                                values: [],
                            });
                        }
                    });

                    const keywordDimensions = ['LST_KEYWORD_LIST', 'BENCHMARKING_KEYWORD_LIST'];

                    keywordDimensions.forEach(dimensionName => {
                        const keywordGroupBys = request.groupBys
                            .filter(
                                groupBy => groupBy.dimensionName.indexOf(dimensionName + '_') === 0
                            )
                            .map(groupBy => groupBy.heading);

                        if (keywordGroupBys.length) {
                            dimensions.push({
                                id: dimensionName,
                                header: keywordGroupBys[0],
                                values: [],
                            });
                        }
                    });

                    posts.forEach(post => {
                        dimensions.forEach(dimension => {
                            const projections = post.projections;
                            if (projections) {
                                // Replace key like "5a047151e4b02720b585d6ae" with label
                                request.groupBys.forEach(groupBy => {
                                    // Custom groupBys get a "details.displayName" added, leverage that
                                    const displayName =
                                        groupBy.details && groupBy.details.displayName;
                                    if (displayName) {
                                        const key = groupBy.details.fieldName;
                                        if (projections[key]) {
                                            projections[displayName] = projections[key];
                                            delete projections[key];
                                        }
                                    }
                                });

                                let projection = projections[dimension.header];

                                // Projection header can be either. ie "Campaign" or "CAMPAIGN_ID"
                                if (!projection && projections[dimension.id]) {
                                    projection = projections[dimension.id];
                                    dimension.header = dimension.id;
                                }

                                if (projection && projection.value) {
                                    dimension.values.push(projection.value);
                                    doLookup = true;
                                }
                            }
                        });
                    });

                    // Resolve the ids to full names
                    if (doLookup) {
                        // Filter out dimensions for lookup without values
                        dimensions = dimensions.filter(dimension => !!dimension.values.length);

                        return this.sprinklrAPIService
                            .bulkLookupMulti(
                                request.reportingEngine as any,
                                request.report,
                                dimensions.map(
                                    dimension =>
                                        new WidgetDimensionKey(
                                            dimension.id as any,
                                            dimension.fieldName,
                                            dimension.srcType
                                        )
                                ),
                                dimensions.map(dimension => dimension.values),
                                null,
                                cancelSource
                            )
                            .then(response => {
                                posts.forEach(post => {
                                    dimensions.forEach(dimension => {
                                        const data = response[dimension.fieldName || dimension.id];
                                        if (data) {
                                            const id =
                                                post.projections[dimension.header] &&
                                                post.projections[dimension.header].value;
                                            if (id && data[id]) {
                                                // contactInfo for ADVOCATES response
                                                const name =
                                                    (data[id].contactInfo &&
                                                        data[id].contactInfo.fullName) ||
                                                    data[id].displayName ||
                                                    data[id].name ||
                                                    data[id];
                                                if (name) {
                                                    post.projections[dimension.header].value = name;
                                                }
                                            }
                                        }
                                    });
                                });

                                return { posts: posts, postIds: postIds };
                            });
                    }
                }

                return { posts: posts, postIds: postIds };
            });
        });

        promise.cancel = function(message?: string) {
            cancelSource.cancel(message);
        };

        return promise;
    }

    private buildDecor(
        request: PostsRequest,
        offset: number,
        fields: any[],
        extras: any[],
        value: any,
        origOrder: number
    ): ProjectionDecor {
        const result: ProjectionDecor = { value: value, origOrder, additional: false };
        const field = fields[offset];

        // This is set for projections
        result.additional = field?.details?.additional;

        // Check for groupBys
        if (!result.additional) {
            result.additional = !!extras.find(
                item => item.dimensionName && item.dimensionName === field?.dimensionName
            );
        }

        let dataType = field?.details?.dataType;
        if (dataType) {
            // Special case for generic currency types.  Use the value
            // specified by the user.
            if (dataType === 'CURRENCY' && request.additional?.Currency) {
                dataType = 'CURRENCY_' + request.additional.Currency;
            }

            result.dataType = dataType;
        } else {
            dataType = field?.aggregateFunction;
            if (dataType) {
                result.dataType = dataType;
            }
        }
        if (field?.details?.alternateHeading) {
            result.alternateHeading = field.details.alternateHeading;
        } else if (field?.details?.label) {
            result.alternateHeading = field?.details?.label;
        } else if (field?.details?.displayName) {
            result.alternateHeading = field.details.displayName;
        }

        return result;
    }

    // Used when metrics use CHANGE, PERCENTAGE_CHANGE, or PERCENTAGE.
    // This will truncate the data.headings and rows to remove extraneous
    // data for each metric
    private truncateForDecorators(
        projection: AnalyticsProjection,
        decorators: string[],
        data: any
    ) {
        const aggregate = projection.aggregateFunction;
        const convert = decorators.indexOf(aggregate) != -1;
        const heading = projection.heading + (convert ? '_' + aggregate : '');
        const found = data.headings.indexOf(projection.heading + (convert ? '_' + aggregate : ''));
        if (found != -1) {
            // No matter what order the projectionDecorations are in, the
            // results come back in this pre-defined order:
            //   CHANGE, PERCENTAGE_CHANGE, PERCENTAGE
            const removeBefore = convert ? decorators.indexOf(aggregate) + 1 : 0;
            const removeAfter = decorators.length - removeBefore;

            data.headings[found] = projection.heading;
            data.headings.splice(found + 1, removeAfter);
            data.headings.splice(found - removeBefore, removeBefore);
            data.rows.forEach((row: any[]) => {
                row.splice(found + 1, removeAfter);
                row.splice(found - removeBefore, removeBefore);
            });

            // Overwrite the existing non-decorated heading with the new decorated one
            projection.heading = heading;
        }
    }

    /**
     *
     * @param postIds
     * @param projections
     * @param fieldName
     * @param engine
     * @param report
     * @param timePeriod
     * @param cancelSource
     * @returns {any}
     */
    getPostsById(
        postIds: string[],
        projections: { [name: string]: { [name: string]: any } },
        fieldName: WidgetDimensionKey,
        engine: AnalyticsEngine,
        report: string,
        timePeriod?: TimePeriod,
        cancelSource?: CancelTokenSource
    ): PromiseWithCancel<any[]> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        const promise: any = this.sprinklrAPIService
            .bulkLookup(engine, report, fieldName, postIds, timePeriod, cancelSource)
            .then(postsMap => {
                if (!postsMap) {
                    return [];
                }

                const posts = postIds
                    .filter((postId: any) => {
                        return postId in postsMap;
                    })
                    .map((postId: any) => {
                        const post = postsMap[postId] as any;
                        if (projections && projections[postId]) {
                            post.projections = projections[postId];
                        }
                        return post;
                    });

                // paid and platform are missing senderProfile, so we'll need to grab it
                // the second part of the check is meant to "future-proof" this
                // https://sprinklr.atlassian.net/browse/DISPLAY-4817
                if (
                    !['PAID', 'PLATFORM'].includes(engine) ||
                    posts.every(post => post.senderProfile)
                ) {
                    return posts;
                }

                const authorIds = posts
                    .map((post: any) => {
                        return post.accountId;
                    })
                    .filter((value, index, self) => {
                        return value && self.indexOf(value) === index;
                    });

                return this.sprinklrAPIService
                    .bulkLookup(
                        engine,
                        report,
                        new WidgetDimensionKey('ACCOUNT_ID'),
                        authorIds,
                        timePeriod,
                        cancelSource
                    )
                    .then((accountsMap: { [accountId: string]: any }) => {
                        posts.forEach((post: any) => {
                            if (!(post.accountId in accountsMap)) {
                                return;
                            }

                            const account = accountsMap[post.accountId];

                            post.senderProfile = {
                                id: account.id,
                                snType: account.snType,
                                location: account.location,
                                snId: account.id,
                                name: account.displayName,
                                screenName: account.screenName,
                                bio: account.bio,
                                following: account.followingCount,
                                followers: account.followerCount,
                                statusCount: account.statusCount,
                                permalink: account.permalink,
                                profileUrl: account.accountUrl,
                                profileImgUrl: account.profileImgUrl,
                                createdTime: account.createdTime,
                                modifiedTime: account.modifiedTime,
                                clientId: account.clientId,
                                accountId: account.accountId,
                                delFlag: account.isDeleted,
                                accountsFollowedByUser: account.accountFollowedByUser,
                                accountsFollowingUser: account.accountFollowingUser,
                            } as PostProfile;
                        });

                        return posts;
                    });
            });

        promise.cancel = (message?: string) => {
            cancelSource.cancel(message);
        };

        return promise;
    }

    // Used when using splitRequests() to merge the post's projections together
    static combinePosts({
        requests,
        firstResult,
        results,
        requestProjections,
        groupByOffset,
    }: {
        requests: PostsRequest[];
        firstResult: PostResponse;
        results: PostResponse[];
        requestProjections: AnalyticsProjection[];
        groupByOffset: number;
    }): Post[] {
        let posts: Post[] = [];
        posts = posts.concat(firstResult.posts);

        results.forEach((result, offset) => {
            posts.forEach(post => {
                const projections = (post as any).projections;
                const found = result.posts.find(
                    post2 => post.universalId === post2.universalId
                ) as any;
                if (found) {
                    const keys = Object.keys(found.projections);
                    keys.forEach(key => {
                        projections[key] = found.projections[key];
                    });
                } else {
                    requests[offset].projections.forEach(projection => {
                        const key = buildProjectionHeading(
                            projection.measurementName,
                            projection.details?.origReport,
                            projection.aggregateFunction
                        );
                        const origOrder =
                            requestProjections.findIndex(
                                proj =>
                                    buildProjectionHeading(
                                        proj.measurementName,
                                        proj.details?.origReport,
                                        proj.aggregateFunction
                                    ) === key
                            ) + groupByOffset;
                        projections[key] = {
                            value: 0,
                            additional: true,
                            origOrder,
                        };
                    });
                }
            });
        });

        return posts;
    }
}
